import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Fingerprint, CheckCircle, AlertCircle, Loader, Shield, Calendar, Clock } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

// Public Client Intake Form - No authentication required
const ClientIntakePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [intakeInfo, setIntakeInfo] = useState(null);
  const [consent, setConsent] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookingAppointment, setBookingAppointment] = useState(false);
  const [appointmentBooked, setAppointmentBooked] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    aliases: "",
    date_of_birth: "",
    place_of_birth: "",
    sex: "",
    race: "",
    height: "",
    weight: "",
    eyes: "",
    hair: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    ssn_last4: "",
    citizenship: "US Citizen",
    reason: "",
    organization: "",
    occupation: "",
    employer_address: "",
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      // Use the public API without auth
      const response = await axios.get(`/public/intake/${token}/validate`);
      setIntakeInfo(response.data);
      
      // Pre-fill reason if provided
      if (response.data.reason) {
        setFormData(prev => ({ ...prev, reason: response.data.reason }));
      }
    } catch (error) {
      console.error("Invalid intake link:", error);
      setError("This intake link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consent) {
      toast.error("Please agree to the consent statement to continue.");
      return;
    }
    
    // Validate required fields
    if (!formData.first_name.trim()) {
      toast.error("First name is required.");
      return;
    }
    
    if (!formData.last_name.trim()) {
      toast.error("Last name is required.");
      return;
    }
    
    if (!formData.middle_name.trim()) {
      toast.error("Middle name is required. Enter 'NMN' if you have no middle name.");
      return;
    }
    
    if (!formData.date_of_birth) {
      toast.error("Date of birth is required.");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Clean up empty strings to null for optional fields
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && !['first_name', 'last_name', 'middle_name', 'date_of_birth'].includes(key)) {
          cleanedData[key] = null;
        }
      });
      
      await axios.post(`/public/intake/${token}/submit`, cleanedData);
      setSubmitted(true);
      toast.success("Your information has been submitted successfully!");
      
      // Fetch available appointment slots
      try {
        const slotsResp = await axios.get(`/public/intake/${token}/available-slots`);
        setAvailableSlots(slotsResp.data.schedule || []);
        setShowAppointment(true);
      } catch {
        // No slots available, skip appointment step
        setShowAppointment(false);
      }
    } catch (error) {
      console.error("Failed to submit intake:", error);
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Validating intake link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Invalid</h1>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-500 mt-4">
            Please contact your fingerprinting service provider for a new link.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    // Show appointment booking or success
    if (showAppointment && !appointmentBooked) {
      return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
          <Toaster position="top-right" />
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h1 className="text-2xl font-bold text-gray-900">Information Submitted!</h1>
                <p className="text-gray-600 mt-1">Now let's schedule your appointment.</p>
              </div>
              
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Select an Appointment Time
              </h2>
              
              {availableSlots.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Loading available times...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableSlots.map((day) => (
                    <div key={day.date} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-800 mb-3">
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { 
                          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                        })}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {day.slots.filter(s => s.available).map((slot) => {
                          const isSelected = selectedDate === day.date && selectedTime === slot.time;
                          return (
                            <button
                              key={slot.time}
                              data-testid={`slot-${day.date}-${slot.time}`}
                              onClick={() => { setSelectedDate(day.date); setSelectedTime(slot.time); }}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                isSelected
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {slot.time}
                            </button>
                          );
                        })}
                        {day.slots.filter(s => s.available).length === 0 && (
                          <p className="text-sm text-gray-400">No available slots</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 flex gap-3">
                <button
                  data-testid="book-appointment-btn"
                  onClick={async () => {
                    if (!selectedDate || !selectedTime) {
                      toast.error("Please select a date and time.");
                      return;
                    }
                    setBookingAppointment(true);
                    try {
                      await axios.post(`/public/intake/${token}/book-appointment`, {
                        appointment_date: selectedDate,
                        appointment_time: selectedTime,
                        client_name: `${formData.first_name} ${formData.last_name}`,
                        client_phone: formData.phone,
                        client_email: formData.email
                      });
                      setAppointmentBooked(true);
                      toast.success("Appointment booked!");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to book appointment.");
                    } finally {
                      setBookingAppointment(false);
                    }
                  }}
                  disabled={!selectedDate || !selectedTime || bookingAppointment}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors"
                >
                  {bookingAppointment ? "Booking..." : "Book Appointment"}
                </button>
                <button
                  data-testid="skip-appointment-btn"
                  onClick={() => setAppointmentBooked(true)}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 rounded-lg font-medium"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Final success with status tracking link
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {appointmentBooked && selectedDate ? "All Set!" : "Information Submitted!"}
          </h1>
          <p className="text-gray-600">
            {appointmentBooked && selectedDate 
              ? `Your appointment is scheduled for ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at ${selectedTime}.`
              : "Thank you for completing your profile. Your fingerprinting service provider will contact you with next steps."
            }
          </p>
          <div className="mt-6 space-y-3">
            <a
              href={`/status/${token}`}
              data-testid="track-status-link"
              className="block w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              Track Your Status
            </a>
            <p className="text-xs text-gray-500">
              Bookmark the status page to check your progress anytime.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white py-6">
        <div className="max-w-3xl mx-auto px-4 flex items-center space-x-3">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/8bf96fc3-b1b9-4153-935e-e0d5c02560cb/images/b5ffd65d20497bfd9681416db86fdb9eea50ae3581cd0d0b7ff660d2e2271341.png" 
            alt="FingerprintVS Logo" 
            className="w-12 h-12 rounded-lg"
          />
          <div>
            <h1 className="text-2xl font-bold">FingerprintVS</h1>
            <p className="text-blue-200 text-sm">Client Information Form</p>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Info Banner */}
          <div className="bg-blue-50 border-b border-blue-100 p-4">
            <div className="flex items-start space-x-3">
              <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-blue-900">Secure Client Intake Form</h2>
                <p className="text-sm text-blue-700">
                  Please complete all fields accurately. This information will be used to complete your official fingerprint card (FD-258/FD-1164).
                </p>
                {intakeInfo?.organization && (
                  <p className="text-sm text-blue-600 mt-1">
                    Requested by: <strong>{intakeInfo.organization}</strong>
                  </p>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Name Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full text-sm flex items-center justify-center mr-2">1</span>
                Legal Name (as it appears on government ID)
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Middle Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="middle_name"
                    required
                    placeholder="Enter NMN if none"
                    value={formData.middle_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter "NMN" if you have no middle name</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Names / Aliases / Maiden Name
                </label>
                <input
                  type="text"
                  name="aliases"
                  placeholder="List any other names you have used"
                  value={formData.aliases}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Personal Details Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full text-sm flex items-center justify-center mr-2">2</span>
                Personal Details
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    required
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Place of Birth</label>
                  <input
                    type="text"
                    name="place_of_birth"
                    placeholder="City, State/Country"
                    value={formData.place_of_birth}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sex <span className="text-red-500">*</span></label>
                  <select
                    name="sex"
                    required
                    value={formData.sex}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Race</label>
                  <select
                    name="race"
                    value={formData.race}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="W">White</option>
                    <option value="B">Black</option>
                    <option value="A">Asian</option>
                    <option value="I">American Indian</option>
                    <option value="U">Unknown</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                  <input
                    type="text"
                    name="height"
                    placeholder="e.g., 5'10&quot;"
                    value={formData.height}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                  <input
                    type="text"
                    name="weight"
                    placeholder="lbs"
                    value={formData.weight}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eye Color</label>
                  <select
                    name="eyes"
                    value={formData.eyes}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="BLK">Black</option>
                    <option value="BLU">Blue</option>
                    <option value="BRO">Brown</option>
                    <option value="GRY">Gray</option>
                    <option value="GRN">Green</option>
                    <option value="HAZ">Hazel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hair Color</label>
                  <select
                    name="hair"
                    value={formData.hair}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="BLK">Black</option>
                    <option value="BLN">Blonde</option>
                    <option value="BRO">Brown</option>
                    <option value="GRY">Gray</option>
                    <option value="RED">Red</option>
                    <option value="WHI">White</option>
                    <option value="BAL">Bald</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SSN (last 4)</label>
                  <input
                    type="text"
                    name="ssn_last4"
                    maxLength="4"
                    placeholder="XXXX"
                    value={formData.ssn_last4}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Citizenship</label>
                  <select
                    name="citizenship"
                    value={formData.citizenship}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="US Citizen">US Citizen</option>
                    <option value="Permanent Resident">Permanent Resident</option>
                    <option value="Visa Holder">Visa Holder</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full text-sm flex items-center justify-center mr-2">3</span>
                Contact Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full text-sm flex items-center justify-center mr-2">4</span>
                Current Residence Address
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      required
                      maxLength="2"
                      placeholder="e.g., CA"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="zip_code"
                      required
                      value={formData.zip_code}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full text-sm flex items-center justify-center mr-2">5</span>
                Employment / Purpose
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Fingerprinting</label>
                  <input
                    type="text"
                    name="reason"
                    placeholder="e.g., Employment Background Check"
                    value={formData.reason}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employer/Organization</label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employer Address</label>
                  <input
                    type="text"
                    name="employer_address"
                    value={formData.employer_address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Consent Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-yellow-100 text-yellow-800 w-6 h-6 rounded-full text-sm flex items-center justify-center mr-2">!</span>
                Consent & Certification
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  By submitting this form, I certify that:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>All information provided is true and accurate to the best of my knowledge.</li>
                  <li>I understand that providing false information may result in denial of service and potential legal consequences.</li>
                  <li>I consent to having my fingerprints captured and submitted for the purpose stated above.</li>
                  <li>I understand that my fingerprints may be submitted to the FBI and/or state agencies for background check purposes.</li>
                </ul>
              </div>
              <div className="mt-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    I have read and agree to the above consent statement. I certify that all information provided is accurate. <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || !consent}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2"
            >
              {submitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Submit Information</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Secured by FingerprintVS • Your information is encrypted and protected
        </p>
      </main>
    </div>
  );
};

export default ClientIntakePage;
